// @ts-nocheck
import {
  Autocomplete,
  TextField,
  Grid,
  Paper,
  Typography,
  createFilterOptions,
} from "@mui/material";
import React from "react";
import PropTypes from "prop-types";
import { getCssVariableHandler } from "../../utils/styles/getCssVariables";
import { ContactTool } from "../Tooltip/ContactTool";
import { ThirdPartyTag } from "../Tooltip/ThirdPartyTag";
import { getNewlyAssignedContractorColor } from "../../utils/methods/contractors/contractorUtils";
import { getUniversalDateTimeFormat } from "../../appCalcutions";

// Local filter used when the dropdown has no server-side search. Matches on the
// same `name` field the option list renders, so typing narrows what the user sees.
const localNameFilter = createFilterOptions({
  stringify: (option) => option?.name ?? "",
});

const LoadingText = () => {
  return (
    <Typography
      sx={{
        color: getCssVariableHandler({
          variableName: "--font-rgb",
        }),
      }}
    >
      Loading...
    </Typography>
  );
};

const UserIdDropdown = ({
  name,
  userId,
  fetchOptions,
  onChange,
  isLoading,
  placeholder,
  fixOptions,
  fontSize = "16px",
  unCleanable = true,
  onBlurHandler = () => {},
  sx,
  showDateFields = false,
}) => {
  const [input, setInput] = React.useState("");
  const [value, setValue] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const isFirstRenderRef = React.useRef(true);
  const justSelectedRef = React.useRef(false);

  React.useEffect(() => {
    if (name && userId) {
      setValue({ name, _id: userId });
    } else {
      setValue(null);
    }
  }, [name, userId]);

  React.useEffect(() => {
    if (isFirstRenderRef.current) {
      if (input === "") return;
      isFirstRenderRef.current = false;
      return;
    }

    // Don't trigger search if user just selected an option
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    // Callers that rely on a fixed option list do not pass fetchOptions.
    if (typeof fetchOptions !== "function") return;

    const debounce = setTimeout(() => {
      fetchOptions(input, setOptions);
    }, [300]);

    return () => {
      clearTimeout(debounce);
    };

    // eslint-disable-next-line
  }, [input]);

  return (
    <Autocomplete
      className="coordinator-search"
      ref={isFirstRenderRef}
      onChange={(_, newValue) => {
        onChange(newValue);
        setValue(newValue);
      }}
      disableClearable={unCleanable ?? true}
      fullWidth
      options={fixOptions || options || []}
      value={value}
      inputValue={input}
      loading={isLoading}
      loadingText={<LoadingText />}
      autoHighlight
      noOptionsText={
        <Typography sx={{ color: "white" }}>No Options Available</Typography>
      }
      onBlur={onBlurHandler}
      onInputChange={(_, newValue) => {
        // const value = newValue?.replace(inputRegex, "");
        setInput(newValue);
        if (newValue === "") {
          setOptions(fixOptions ?? []);
        }
      }}
      getOptionLabel={(option) => {
        return option?.name;
      }}
      isOptionEqualToValue={(option, value) => option?._id === value?._id}
      renderInput={(params) => (
        <TextField
          placeholder={placeholder}
          {...params}
          sx={{
            "& input": {
              color: "#D175B6",
              fontSize: "18px",
              // textDecoration: "underline",
            },
            "& .MuiOutlinedInput-root": {
              // padding: "0",
              "& fieldset": {
                borderColor: "#D175B6 !important",
                borderRadius: "8px",
              },
              "&:hover fieldset": {
                borderColor: "#D175B6",
                borderRadius: "8px",
              },
            },
            ...(sx || {}),
          }}
        />
      )}
      renderOption={(props, options) => {
        return (
          <Grid
            key={options?._id}
            container
            style={{
              gap: "10px",
              border: "1px solid #D175B6",
              borderRadius: "12px",
              marginBottom: "10px",
              ...(showDateFields
                ? {
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: "0.25rem",
                    flexDirection: "column",
                  }
                : {}),
            }}
            {...props}
          >
            <Typography sx={{ fontSize: fontSize }}>{options?.name}</Typography>
            {showDateFields ? (
              <Typography
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "1.5rem",
                }}
              >
                <Typography>
                  Event Start:{" "}
                  {getUniversalDateTimeFormat({
                    dateTime: options?.eventStart,
                  })}
                </Typography>

                <Typography>
                  Event End:{" "}
                  {getUniversalDateTimeFormat({
                    dateTime: options?.eventEnd,
                  })}
                </Typography>
              </Typography>
            ) : null}
          </Grid>
        );
      }}
      PaperComponent={({ children }) => (
        <Paper
          style={{
            backgroundColor: "rgba(0, 0, 0,1)",
            borderRadius: "12px",
            border: "1px solid #D175B6",
            zIndex: 20,
            // width: "200px",
            padding: "5px 8px",
            color: "white",
            whiteSpace: "no-wrap",
            margin: "auto",
          }}
        >
          {children}
        </Paper>
      )}
      ListboxProps={{
        className: "custom-scroll-design",
      }}
    />
  );
};

export const UserIdFetchDropdown = ({
  name,
  userId,
  fetchOptions,
  onChange,
  isLoading,
  placeholder,
  fixOptions,
  fontSize = "16px",
  unCleanable = true,
  onBlurHandler = () => {},
  sx,
}) => {
  const [input, setInput] = React.useState("");
  const [value, setValue] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const isFirstRenderRef = React.useRef(true);
  const justSelectedRef = React.useRef(false);

  React.useEffect(() => {
    if (name && userId) {
      setValue({ name, _id: userId });
    } else {
      setValue(null);
    }
  }, [name, userId]);

  React.useEffect(() => {
    if (isFirstRenderRef.current) {
      if (input === "") return;
      isFirstRenderRef.current = false;
      return;
    }

    // Don't trigger search if user just selected an option
    if (justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }

    // Callers that rely on a fixed option list do not pass fetchOptions.
    if (typeof fetchOptions !== "function") return;

    const debounce = setTimeout(() => {
      fetchOptions(input, setOptions);
    }, [300]);

    return () => {
      clearTimeout(debounce);
    };

    // eslint-disable-next-line
  }, [input]);

  return (
    <Autocomplete
      className="coordinator-search"
      ref={isFirstRenderRef}
      onChange={(_, newValue) => {
        justSelectedRef.current = true;
        onChange(newValue);
        setValue(newValue);
      }}
      disableClearable={unCleanable ?? true}
      fullWidth
      options={fixOptions || options || []}
      value={value}
      inputValue={input}
      loading={isLoading}
      loadingText={<LoadingText />}
      // With server-side search the options are already filtered, so keep them as
      // they are. Without it, fall back to filtering the fixed list by name --
      // otherwise typing does nothing and the full list stays on screen.
      filterOptions={
        typeof fetchOptions === "function"
          ? (options) => options
          : localNameFilter
      }
      autoHighlight
      noOptionsText={
        <Typography sx={{ color: "white" }}>No Options Available</Typography>
      }
      onBlur={onBlurHandler}
      onInputChange={(_, newValue) => {
        // const value = newValue?.replace(inputRegex, "");
        setInput(newValue);
        if (newValue === "") {
          setOptions(fixOptions ?? []);
        }
      }}
      getOptionLabel={(option) => {
        return option?.name;
      }}
      isOptionEqualToValue={(option, value) => option?._id === value?._id}
      renderInput={(params) => (
        <TextField
          placeholder={placeholder}
          {...params}
          sx={{
            "& input": {
              color: "#D175B6",
              fontSize: "18px",
              // textDecoration: "underline",
            },
            "& .MuiOutlinedInput-root": {
              // padding: "0",
              "& fieldset": {
                borderColor: "#D175B6 !important",
                borderRadius: "8px",
              },
              "&:hover fieldset": {
                borderColor: "#D175B6",
                borderRadius: "8px",
              },
            },
            ...(sx || {}),
          }}
        />
      )}
      renderOption={(props, options) => {
        return (
          <Grid
            key={options?._id}
            container
            style={{
              gap: "10px",
              border: "1px solid #D175B6",
              borderRadius: "12px",
              marginBottom: "10px",
            }}
            {...props}
          >
            <ContactTool
              showName
              data={options?.hoverDetails}
              rowData={options?.hoverDetails}
              isDM
              showConsentBtns
            >
              <Typography
                sx={{
                  fontSize: fontSize,
                  color:
                    getNewlyAssignedContractorColor({
                      data: options?.hoverDetails,
                    }) || "inherit",
                }}
              >
                {options?.name}
                <ThirdPartyTag contractorType={options?.contractorType} />
              </Typography>
            </ContactTool>
          </Grid>
        );
      }}
      PaperComponent={({ children }) => (
        <Paper
          style={{
            backgroundColor: "rgba(0, 0, 0,1)",
            borderRadius: "12px",
            border: "1px solid #D175B6",
            zIndex: 20,
            // width: "200px",
            padding: "5px 8px",
            color: "white",
            whiteSpace: "no-wrap",
            margin: "auto",
          }}
        >
          {children}
        </Paper>
      )}
      ListboxProps={{
        className: "custom-scroll-design",
      }}
    />
  );
};

UserIdDropdown.propTypes = {
  name: PropTypes.string,
  userId: PropTypes.string,
  fetchOptions: PropTypes.func,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  fixOptions: PropTypes.array,
  fontSize: PropTypes.string,
  unCleanable: PropTypes.bool,
};

UserIdFetchDropdown.propTypes = {
  name: PropTypes.string,
  userId: PropTypes.string,
  fetchOptions: PropTypes.func,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  fixOptions: PropTypes.array,
  fontSize: PropTypes.string,
  unCleanable: PropTypes.bool,
};

export default UserIdDropdown;